import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  HelpCircle,
  MessageCircle,
  Mail,
  Clock,
  CheckCircle,
  BookOpen,
  Video,
  FileText,
  Users,
  Package,
  FileText as FileTextIcon,
  Calculator,
  BookOpen as BookOpenIcon,
  Archive,
  DollarSign,
  Calendar,
  Ruler,
  BarChart3,
  Receipt,
  Building2,
  Wrench,
  ArrowRight,
  Sparkles,
  Upload,
  Download,
  Trash2,
  Link2,
  Zap,
  CreditCard,
  UserCircle as User,
  Share2,
  Gift,
  MessageSquare as ConfiguracionesWhatsApp,
  ChevronDown
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";

export default function Ajuda() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent("Olá! Preciso de ajuda com o Ateliê PRO. Podem me ajudar?");
    window.open(`https://wa.me/5561982321534?text=${message}`, '_blank');
  };

  const handleEmailContact = () => {
    window.open('mailto:suporte@ateliepro.online?subject=Dúvida sobre o Ateliê PRO', '_blank');
  };

  const funcionalidades = [
    {
      icon: Package,
      title: "Dashboard",
      description: "Sua central de comando do Ateliê PRO",
      features: [
        "Visão geral do seu negócio em tempo real",
        "Principais métricas e estatísticas",
        "Atalhos rápidos para criar pedidos e orçamentos",
        "Alertas de entregas vencidas",
        "Timeline de atividades recentes",
        "Indicadores de uso e economia de tempo",
        "Banner de trial com contagem regressiva"
      ],
      url: "/"
    },
    {
      icon: Package,
      title: "Pedidos",
      description: "Gerencie todos os seus pedidos de forma completa",
      features: [
        "Criação e edição de pedidos",
        "Controle de status (Aguardando, Em produção, Pronto, Entregue, Cancelado)",
        "Controle de pagamentos (Pendente, Parcial, Pago)",
        "Soma automática de valores (produtos do catálogo + serviços rápidos)",
        "Geração de ordens de produção em PDF",
        "Histórico completo de pedidos",
        "Filtros avançados por status, cliente, data",
        "Impressão de lista de pedidos formatada"
      ],
      url: "/pedidos"
    },
    {
      icon: FileTextIcon,
      title: "Orçamentos",
      description: "Crie e gerencie orçamentos profissionais",
      features: [
        "Criação de orçamentos detalhados",
        "Lista de itens com quantidades e valores",
        "Geração de PDFs para impressão",
        "Compartilhamento público de orçamentos",
        "Envio via WhatsApp",
        "Conversão de orçamento em pedido",
        "Personalização com logo e informações da empresa",
        "Código único por orçamento para rastreamento"
      ],
      url: "/orcamentos"
    },
    {
      icon: BookOpenIcon,
      title: "Catálogo de Produtos",
      description: "Gerencie seu catálogo completo de produtos e serviços",
      features: [
        "Cadastro de produtos com preços, materiais e horas de trabalho",
        "Cadastro de serviços rápidos",
        "Importação em massa via CSV (até 1000 produtos)",
        "Exportação do catálogo para CSV",
        "Exclusão em massa de produtos",
        "Vinculação de estoque em massa",
        "Vinculação automática de estoque durante importação",
        "Categorização e organização",
        "Imagens e descrições detalhadas"
      ],
      url: "/catalogo"
    },
    {
      icon: Archive,
      title: "Estoque",
      description: "Controle completo do seu inventário",
      features: [
        "Controle de inventário de materiais e produtos",
        "Alertas de estoque baixo",
        "Importação em massa via CSV",
        "Exclusão em massa de itens",
        "Movimentações de entrada e saída",
        "Histórico de movimentações",
        "Categorização por tipo (Matéria-prima, Tecido, Produto acabado)",
        "Controle de fornecedores e custos",
        "Subtract automático ao vender produtos vinculados"
      ],
      url: "/estoque"
    },
    {
      icon: Users,
      title: "Clientes",
      description: "Gerencie sua base de clientes",
      features: [
        "Cadastro completo de clientes",
        "Histórico de pedidos por cliente",
        "Importação em massa via CSV",
        "Contatos e informações detalhadas",
        "Medidas personalizadas por cliente",
        "Busca e filtros avançados",
        "Tags e categorização de clientes",
        "Integração com WhatsApp para contato"
      ],
      url: "/clientes"
    },
    {
      icon: Ruler,
      title: "Medidas de Clientes",
      description: "Armazene medidas personalizadas",
      features: [
        "Cadastro de medidas por cliente",
        "Histórico de medidas",
        "Organização e busca",
        "Uso em pedidos personalizados",
        "Múltiplas medidas por cliente",
        "Edição rápida de medidas",
        "Documentação de preferências de clientes"
      ],
      url: "/medidas"
    },
    {
      icon: Wrench,
      title: "Serviços",
      description: "Gerencie seus serviços oferecidos",
      features: [
        "Cadastro de serviços",
        "Preços e descrições",
        "Importação em massa via CSV",
        "Categorização",
        "Uso em pedidos e orçamentos",
        "Serviços rápidos com valores fixos"
      ],
      url: "/servicos"
    },
    {
      icon: Calendar,
      title: "Agenda",
      description: "Organize entregas e compromissos",
      features: [
        "Visualização de entregas por data",
        "Lembretes automáticos via WhatsApp",
        "Controle de prazos",
        "Calendário visual",
        "Filtros por período",
        "Notificações de atrasos"
      ],
      url: "/agenda"
    },
    {
      icon: DollarSign,
      title: "Financeiro",
      description: "Controle financeiro completo",
      features: [
        "Registro de receitas e pagamentos",
        "Controle de valores pagos por pedido",
        "Relatórios financeiros detalhados",
        "Lembretes de pagamento via WhatsApp",
        "Fluxo de caixa com abas (Saldos e Saldo por Conta)",
        "Contas a pagar e receber",
        "Gráficos e estatísticas",
        "Integração com gateway de pagamento Asaas",
        "Card de Saúde Financeira no Dashboard",
        "Sistema de Alertas para vencimentos próximos",
        "Abas de Controle Financeiro (Pedidos e Contas Manuais)",
        "Filtros compactados e reorganizados"
      ],
      url: "/financeiro"
    },
    {
      icon: Building2,
      title: "Fornecedores",
      description: "Gerencie seus fornecedores",
      features: [
        "Cadastro de fornecedores",
        "Pedidos de compra",
        "Histórico de compras",
        "Contatos e informações",
        "Organização por categoria",
        "Controle de custos"
      ],
      url: "/fornecedores"
    },
    {
      icon: Calculator,
      title: "Calculadora de Preços",
      description: "Calcule preços de forma precisa",
      features: [
        "Cálculo baseado em materiais",
        "Horas de trabalho",
        "Margem de lucro",
        "Custos variáveis",
        "Histórico de cálculos",
        "Integração com catálogo de produtos",
        "Exportação para orçamento"
      ],
      url: "/calculadora"
    },
    {
      icon: BarChart3,
      title: "Relatórios",
      description: "Análises e estatísticas do seu negócio",
      features: [
        "Relatórios de vendas",
        "Relatórios financeiros",
        "Relatórios de estoque",
        "Gráficos e visualizações",
        "Exportação de dados",
        "Filtros por período",
        "Análise de desempenho por cliente",
        "Trending e tendências"
      ],
      url: "/relatorios"
    },
    {
      icon: Receipt,
      title: "Notas Fiscais",
      description: "Emissão e gestão de notas fiscais",
      features: [
        "Integração com Focus NF",
        "Emissão de notas fiscais",
        "Gestão completa de NF-e",
        "Histórico e consultas",
        "Configuração de certificados",
        "Rastreamento de notas emitidas"
      ],
      url: "/notas-fiscais",
      requiresNF: true
    },
    {
      icon: CreditCard,
      title: "Assinatura e Planos",
      description: "Gerencie sua assinatura e escolha o melhor plano",
      features: [
        "Visualização do plano atual",
        "Upgrade para planos superiores",
        "Comparação entre planos",
        "Formas de pagamento (PIX, Cartão, Boleto)",
        "PIX Automático para renovação",
        "Histórico de pagamentos",
        "Cancelamento de assinatura",
        "Gestão de forma de pagamento"
      ],
      url: "/assinatura"
    },
    {
      icon: User,
      title: "Minha Conta",
      description: "Gerencie suas informações pessoais e da empresa",
      features: [
        "Edição de dados pessoais",
        "Informações da empresa",
        "Logo e branding",
        "Configurações de email",
        "Histórico de atividades",
        "Preferências de notificações",
        "Segurança e senhas"
      ],
      url: "/minha-conta"
    },
    {
      icon: Share2,
      title: "Indicações",
      description: "Ganhe comissões indicando o Ateliê PRO",
      features: [
        "Seu link único de referência",
        "Histórico de indicações",
        "Comissões ganhas",
        "Rastreamento de conversões",
        "Material de divulgação",
        "Estatísticas de desempenho",
        "Extratos de comissão"
      ],
      url: "/indicacoes"
    },
    {
      icon: Gift,
      title: "Recompensas",
      description: "Acumule pontos e ganhe benefícios",
      features: [
        "Pontos por atividades (pedidos, orçamentos)",
        "Resgate de recompensas",
        "Descontos especiais",
        "Acesso a recursos premium",
        "Bônus mensais",
        "Ranking de usuários",
        "Histórico de resgate"
      ],
      url: "/recompensas"
    },
    {
      icon: BarChart3,
      title: "Relatório de Uso",
      description: "Acompanhe seu uso do sistema",
      features: [
        "Estatísticas de uso diário/mensal",
        "Horas economizadas estimadas",
        "Valor processado",
        "Número de pedidos e orçamentos",
        "Clientes cadastrados",
        "Análise de ROI",
        "Comparação com períodos anteriores"
      ],
      url: "/relatorio-uso"
    },
    {
      icon: ConfiguracionesWhatsApp,
      title: "Configuração WhatsApp",
      description: "Integre o WhatsApp para automações",
      features: [
        "Conexão com API WhatsApp",
        "Envio automático de orçamentos",
        "Lembretes de entregas",
        "Notificações de pedidos",
        "Confirmação de pagamentos",
        "Respostas automáticas",
        "Histórico de mensagens"
      ],
      url: "/config-whatsapp"
    },
    {
      icon: FileText,
      title: "Notas Fiscais - Configuração",
      description: "Configure a emissão de notas fiscais",
      features: [
        "Integração com Focus NF",
        "Carregamento de certificado digital",
        "Configuração de dados fiscais",
        "Teste de conexão",
        "Histórico de configurações",
        "Suporte técnico integrado"
      ],
      url: "/config-focus-nf"
    },
    {
      icon: Zap,
      title: "FAQ e Perguntas Frequentes",
      description: "Respostas para as dúvidas mais comuns",
      features: [
        "Respostas rápidas e diretas",
        "Guias passo a passo",
        "Dicas e truques",
        "Troubleshooting comum",
        "Atualizações e novidades",
        "Compatibilidade e requisitos"
      ],
      url: "/faq"
    }
  ];

  const faqItems = [
    {
      question: "Como faço para criar meu primeiro pedido?",
      answer: "Acesse a seção 'Pedidos' no menu lateral e clique em 'Novo Pedido'. Preencha as informações do cliente, adicione produtos do catálogo ou serviços rápidos. O valor será calculado automaticamente!"
    },
    {
      question: "Como funciona a soma automática de valores no pedido?",
      answer: "Quando você adiciona produtos do catálogo ou serviços rápidos, o sistema soma automaticamente todos os valores. O campo de valor total é atualizado em tempo real. Você pode editar manualmente se necessário."
    },
    {
      question: "Como importar produtos em massa?",
      answer: "Na página 'Catálogo', clique em 'Importar Produtos'. Baixe o arquivo de exemplo CSV, preencha com seus produtos e faça o upload. Você pode importar até 1000 produtos de uma vez. O sistema também permite vincular estoque automaticamente durante a importação!"
    },
    {
      question: "Como exportar o catálogo para CSV?",
      answer: "Na página 'Catálogo', clique no botão 'Exportar CSV' no topo da página. O arquivo será baixado com todos os produtos e suas informações, incluindo vínculos de estoque."
    },
    {
      question: "Como fazer exclusão em massa?",
      answer: "Tanto no Catálogo quanto no Estoque, clique no botão 'Selecionar', marque os itens desejados usando os checkboxes, e depois clique em 'Excluir Selecionados'. Confirme a ação."
    },
    {
      question: "Como vincular estoque em massa?",
      answer: "No Catálogo, selecione múltiplos produtos usando o modo de seleção, depois clique em 'Vincular Estoque'. Escolha o item de estoque e a quantidade por unidade. Todos os produtos selecionados serão vinculados de uma vez!"
    },
    {
      question: "Como funciona a vinculação automática de estoque na importação?",
      answer: "Ao importar produtos via CSV, adicione as colunas 'Item Estoque' (nome do item) e 'Quantidade por Unidade'. O sistema buscará automaticamente o item no estoque e criará o vínculo durante a importação."
    },
    {
      question: "Posso personalizar os orçamentos?",
      answer: "Sim! Na seção 'Orçamentos' você pode criar orçamentos personalizados com sua logo e informações da empresa. Os orçamentos podem ser enviados por WhatsApp ou compartilhados via link público."
    },
    {
      question: "Como funciona o controle de estoque?",
      answer: "O sistema permite controlar materiais e produtos. Você pode adicionar itens manualmente ou importar via CSV. Quando um produto vendido está vinculado ao estoque, a quantidade é subtraída automaticamente. Configure alertas de estoque baixo!"
    },
    {
      question: "Posso integrar com WhatsApp?",
      answer: "Sim! Configure na seção 'Config. WhatsApp'. O sistema permite enviar orçamentos, pedidos e lembretes diretamente pelo WhatsApp para seus clientes."
    },
    {
      question: "Como funciona o fluxo de caixa?",
      answer: "Na seção 'Fluxo de Caixa' você gerencia contas a pagar e receber com uma interface completa. Use a aba 'Saldos' para ver o resumo (Saldo Atual, Pagar, Receber) e a aba 'Saldo por Conta' para detalhar as contas e formas de pagamento. Os filtros compactados (Período, Tipo, Status, Conta, Forma, Busca) permitem visualizar exatamente o que você precisa. O card de dicas no topo orienta novos usuários."
    },
    {
      question: "O que são os Alertas Financeiros?",
      answer: "Novo sistema de notificações que acompanha seus vencimentos. Clique no ícone de sino (🔔) no header para ver alertas de contas atrasadas, vencimentos hoje e próximos vencimentos (até 3 dias). Os alertas aparecem categorizados por tipo e mostram o valor de cada conta para você tomar decisões informadas."
    },
    {
      question: "Como funciona o Card de Saúde Financeira?",
      answer: "No Dashboard, você verá um card que avalia a saúde do seu negócio. Mostra se está 'Saudável' ou 'Atenção' baseado em: Saldo atual, Contas a Receber, Contas a Pagar e Contas Atrasadas. Clique em cada métrica para navegar direto para a página com mais detalhes."
    },
    {
      question: "Como usar a calculadora de preços?",
      answer: "A calculadora ajuda a definir preços baseados em materiais, horas de trabalho e margem de lucro. Informe os custos e o sistema calculará o preço final sugerido."
    },
    {
      question: "Como faço backup dos meus dados?",
      answer: "Seus dados são automaticamente salvos na nuvem. Você pode exportar relatórios e o catálogo em CSV para ter backups locais. Usuários premium têm recursos adicionais de backup."
    },
    {
      question: "Posso usar em múltiplos dispositivos?",
      answer: "Sim! O Ateliê PRO funciona em qualquer dispositivo com navegador - computador, tablet ou celular. Todos os dados são sincronizados automaticamente."
    },
    {
      question: "Como emitir notas fiscais?",
      answer: "A emissão de notas fiscais está disponível no plano Profissional. Configure a integração com Focus NF na seção 'Config. Notas Fiscais' e comece a emitir suas NF-e."
    }
  ];

  const novidades = [
    {
      icon: Zap,
      title: "Alertas Financeiros",
      description: "Sistema inteligente de notificações que avisa sobre vencimentos próximos, contas atrasadas e oferece acesso rápido às páginas financeiras."
    },
    {
      icon: BarChart3,
      title: "Card de Saúde Financeira",
      description: "Dashboard visual que mostra a saúde do seu negócio em tempo real com métricas de saldo, receitas e despesas. Clicável para navegar direto aos detalhes."
    },
    {
      icon: Zap,
      title: "Abas no Fluxo de Caixa",
      description: "Organize melhor seus dados com abas: 'Saldos' mostra o resumo, 'Saldo por Conta' mostra o detalhe de cada conta e forma de pagamento."
    },
    {
      icon: Wrench,
      title: "Filtros Compactados",
      description: "Interface reorganizada do Fluxo de Caixa com filtros em linha única (Período, Tipo, Status, Conta, Forma, Busca) para melhor visualização."
    },
    {
      icon: Zap,
      title: "Abas de Controle Financeiro",
      description: "Aba 'Pedidos' e 'Contas Manuais' para separar e organizar melhor as contas que você registra manualmente."
    },
    {
      icon: Zap,
      title: "Soma Automática de Valores",
      description: "O sistema agora soma automaticamente produtos do catálogo e serviços rápidos ao criar pedidos."
    },
    {
      icon: Upload,
      title: "Importação Inteligente",
      description: "Importe produtos com vinculação automática de estoque durante a importação via CSV."
    },
    {
      icon: Download,
      title: "Exportação de Catálogo",
      description: "Exporte todo o seu catálogo para CSV com um clique, incluindo vínculos de estoque."
    },
    {
      icon: Trash2,
      title: "Exclusão em Massa",
      description: "Exclua múltiplos produtos ou itens de estoque de uma vez, economizando tempo."
    },
    {
      icon: Link2,
      title: "Vinculação em Massa",
      description: "Vincule o mesmo item de estoque a múltiplos produtos simultaneamente."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Central de Ajuda"
        description="Encontre respostas para suas dúvidas e entre em contato conosco"
        icon={<HelpCircle className="w-6 h-6 text-purple-600" />}
      />

      <div className="p-6 space-y-6">
        {/* Guias em PDF */}
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              📘 Guias Completos em PDF
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Guia 1 */}
              <div className="p-5 bg-white rounded-lg border-2 border-purple-200 shadow-sm hover:shadow-lg transition-all hover:border-purple-400">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">Guia Completo de Uso</h3>
                    <p className="text-xs text-muted-foreground mt-1">📄 14 páginas</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  O caminho certo para cadastrar clientes, controlar estoque, orçar, vender e acompanhar o lucro do seu ateliê — passo a passo.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    6 módulos conectados
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Exemplo prático completo
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Dicas de ouro e erros comuns
                  </div>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  onClick={() => window.open('/guias/Guia-de-Uso-Atelie-Pro.pdf', '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Guia
                </Button>
              </div>

              {/* Guia 2 */}
              <div className="p-5 bg-white rounded-lg border-2 border-purple-200 shadow-sm hover:shadow-lg transition-all hover:border-purple-400">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                    <Link2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">Vincular Materiais & Baixa Automática</h3>
                    <p className="text-xs text-muted-foreground mt-1">📄 12 páginas</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Como diminuir automaticamente tecido, linha e outros materiais do estoque cada vez que uma peça é vendida.
                </p>
                <div className="space-y-2 mb-4">
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    4 passos práticos
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Exemplo real: bordado em camisetas
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    FAQ + checklist final
                  </div>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  onClick={() => window.open('/guias/Guia-Vincular-Materiais-Atelie-Pro.pdf', '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Guia
                </Button>
              </div>
            </div>
            <div className="mt-4 p-3 bg-purple-100 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-900">
                💡 <strong>Dica:</strong> Baixe os guias e consulte sempre que tiver dúvidas. Recomendamos ler o Guia Completo primeiro, depois o de Materiais.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contato Rápido */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-600" />
              Precisa de Ajuda Imediata?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
                <h3 className="font-semibold text-green-900">💬 WhatsApp Suporte</h3>
                <p className="text-sm text-green-800">
                  Resposta rápida. Atendimento seg-sex, 8h às 18h.
                </p>
                <Button
                  onClick={handleWhatsAppContact}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Falar no WhatsApp
                </Button>
              </div>

              <div className="space-y-3 p-4 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
                <h3 className="font-semibold text-purple-900">📧 Email Suporte</h3>
                <p className="text-sm text-purple-800">
                  Envie sua dúvida. Respondemos em até 24 horas.
                </p>
                <Button
                  onClick={handleEmailContact}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Email
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recursos de Ajuda */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Recursos de Ajuda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div
                className="text-center p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-400 transition-all cursor-pointer"
                onClick={() => navigate('/documentacao')}
              >
                <BookOpen className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <h3 className="font-semibold mb-1 text-purple-900">Documentação</h3>
                <p className="text-sm text-purple-700">Guias e tutoriais</p>
              </div>

              <div
                className="text-center p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-400 transition-all cursor-pointer"
                onClick={() => navigate('/faq')}
              >
                <FileText className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <h3 className="font-semibold mb-1 text-purple-900">FAQ</h3>
                <p className="text-sm text-purple-700">Perguntas frequentes</p>
              </div>

              <div
                className="text-center p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-400 transition-all cursor-pointer"
                onClick={() => window.open('https://www.youtube.com/@AteliêPRO', '_blank')}
              >
                <Video className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <h3 className="font-semibold mb-1 text-purple-900">Vídeos</h3>
                <p className="text-sm text-purple-700">Tutoriais passo a passo</p>
              </div>

              <div
                className="text-center p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-400 transition-all cursor-pointer"
                onClick={() => window.open('https://chat.whatsapp.com/CyOtwlgXpwS581Em78kuc0', '_blank')}
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <h3 className="font-semibold mb-1 text-purple-900">Comunidade</h3>
                <p className="text-sm text-purple-700">Grupo no WhatsApp</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Perguntas Frequentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {faqItems.map((item, index) => (
                <div key={index} className="border border-purple-100 rounded-lg overflow-hidden hover:border-purple-300 transition-colors">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full p-4 text-left hover:bg-purple-50/50 transition-colors flex items-start justify-between"
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                      <h3 className="font-semibold text-foreground">{item.question}</h3>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-purple-600 flex-shrink-0 transition-transform ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-4 py-3 bg-purple-50/30 border-t border-purple-100">
                      <p className="text-sm text-muted-foreground">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Informações de Contato */}
        <Card>
          <CardHeader>
            <CardTitle>Informações de Contato</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold mb-1">WhatsApp</h3>
                <p className="text-sm text-muted-foreground mb-2">+55 (61) 98232-1534</p>
                <Badge variant="secondary">Resposta rápida</Badge>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-1">Email</h3>
                <p className="text-sm text-muted-foreground mb-2">suporte@ateliepro.online</p>
                <Badge variant="secondary">Até 24h</Badge>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold mb-1">Horário</h3>
                <p className="text-sm text-muted-foreground mb-2">Seg - Sex: 8h às 18h</p>
                <Badge variant="secondary">Horário comercial</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Novidades */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Novidades e Melhorias Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {novidades.map((novidade, index) => (
                <div key={index} className="p-4 bg-white rounded-lg border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <novidade.icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-purple-900 mb-1">{novidade.title}</h3>
                      <p className="text-sm text-purple-700">{novidade.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Funcionalidades Principais */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Funcionalidades do Sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {funcionalidades.map((func, index) => (
                <div key={index} className="border-2 border-purple-100 rounded-lg p-5 hover:border-purple-300 hover:shadow-md transition-all bg-gradient-to-br from-white to-purple-50/30">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                      <func.icon className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-purple-900">{func.title}</h3>
                        {func.requiresNF && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            Requer NF
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-purple-700 mb-3">{func.description}</p>
                      <ul className="space-y-1 mb-4">
                        {func.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                        {func.features.length > 3 && (
                          <li className="text-sm text-purple-600 font-medium">+ {func.features.length - 3} mais</li>
                        )}
                      </ul>
                      <Button
                        size="sm"
                        onClick={() => navigate(func.url)}
                        className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        Acessar
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Guias de Onboarding */}
        <Card>
          <CardHeader>
            <CardTitle>🚀 Guias de Onboarding - Comece por aqui!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">1️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Primeiros Passos</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Comece pelo Dashboard, configure suas informações de empresa em "Minha Conta", e explore a interface do sistema.
                    </p>
                    <Button variant="outline" size="sm">Ir para Minha Conta</Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">2️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Cadastre seus Produtos</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Vá para "Catálogo de Produtos" e cadastre seus produtos. Você pode adicionar um por um ou importar vários de uma vez em CSV.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/catalogo')}>
                      Ir para Catálogo
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">3️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Configure seu Estoque</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Cadastre seus materiais e produtos em "Estoque" e vincule-os aos produtos do catálogo para controle automático.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/estoque')}>
                      Ir para Estoque
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-orange-50 to-yellow-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">4️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Adicione seus Clientes</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Crie seus primeiro clientes em "Clientes" e organize suas informações de contato. Você pode importar múltiplos clientes de uma vez.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/clientes')}>
                      Ir para Clientes
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-red-50 to-rose-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">5️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Crie seu Primeiro Pedido</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Vá para "Pedidos" e crie seu primeiro pedido. Escolha um cliente, adicione produtos do seu catálogo e salve!
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/pedidos')}>
                      Ir para Pedidos
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-indigo-50 to-blue-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">6️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Configure Lembretes e Automações</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Em "Config. WhatsApp", configure a integração com WhatsApp para receber lembretes automáticos de pedidos e entregas.
                    </p>
                    <Button variant="outline" size="sm">
                      Ir para Configurações
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50">
                <div className="flex items-start gap-4">
                  <div className="text-2xl">7️⃣</div>
                  <div>
                    <h3 className="font-semibold mb-2">Escolha um Plano Pago (Opcional)</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Explore os planos em "Assinatura" para desbloquear recursos como notas fiscais, relatórios avançados e mais espaço de armazenamento.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/assinatura')}>
                      Ver Planos
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle>🔧 Troubleshooting - Resolução de Problemas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Não consigo fazer login</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Verifique se você digitou o email corretamente</li>
                  <li>• Confirme que você registrou uma conta no Ateliê PRO</li>
                  <li>• Tente resetar sua senha usando o link "Esqueci minha senha"</li>
                  <li>• Limpe o cache do navegador e tente novamente</li>
                  <li>• Entre em contato conosco pelo WhatsApp se persistir</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Meus dados não estão salvando</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Verifique sua conexão com a internet</li>
                  <li>• Aguarde a confirmação de salvamento (✓) antes de sair</li>
                  <li>• Tente recarregar a página (F5 ou Cmd+R)</li>
                  <li>• Teste em outro navegador ou dispositivo</li>
                  <li>• Desative extensões do navegador que possam bloquear requisições</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Importação de CSV não funciona</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Certifique-se que seu arquivo está em formato CSV válido</li>
                  <li>• Baixe o modelo de exemplo e compare a estrutura</li>
                  <li>• Não deixe colunas obrigatórias em branco</li>
                  <li>• Use separador de vírgula (,) e não ponto-e-vírgula</li>
                  <li>• Não deixe caracteres especiais problemáticos nos dados</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Erro ao gerar PDF</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Desative o bloqueador de pop-ups</li>
                  <li>• Tente usar outro navegador</li>
                  <li>• Certifique-se que tem permissão para baixar arquivos</li>
                  <li>• Limpe o cache do navegador</li>
                  <li>• Entre em contato conosco se o problema persistir</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ WhatsApp não está enviando mensagens</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Verifique a configuração da API WhatsApp</li>
                  <li>• Confirme que seu token está ativo</li>
                  <li>• Certifique-se que o número do cliente tem WhatsApp</li>
                  <li>• Verifique se o número está com o código do país (+55)</li>
                  <li>• Recarregue a página e tente novamente</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Sistema está lento</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Verifique sua conexão com a internet</li>
                  <li>• Limpe o cache e cookies do navegador</li>
                  <li>• Feche outras abas ou programas pesados</li>
                  <li>• Tente usar um navegador mais atualizado</li>
                  <li>• Use internet cabeada em vez de WiFi se possível</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2 text-red-700">❌ Estoque não está subtraindo automaticamente</h3>
                <ul className="text-sm text-muted-foreground space-y-2 ml-4">
                  <li>• Verifique se o produto está vinculado ao estoque</li>
                  <li>• Confirme que o status do pedido é "Entregue"</li>
                  <li>• Verifique se há quantidade suficiente em estoque</li>
                  <li>• Recarregue a página após criar o pedido</li>
                  <li>• Verifique o histórico de movimentações no Estoque</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dicas Úteis */}
        <Card>
          <CardHeader>
            <CardTitle>Dicas e Truques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h3 className="font-semibold text-blue-900 mb-2">💡 Organização</h3>
                <p className="text-sm text-blue-800">
                  Use o sistema de categorias para organizar melhor seus produtos e facilitar a busca. Isso acelera muito o processo de criação de pedidos!
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <h3 className="font-semibold text-green-900 mb-2">💡 Automação</h3>
                <p className="text-sm text-green-800">
                  Configure lembretes automáticos via WhatsApp para não esquecer de acompanhar pedidos em produção e entregas.
                </p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <h3 className="font-semibold text-purple-900 mb-2">💡 Análise</h3>
                <p className="text-sm text-purple-800">
                  Use os relatórios financeiros regularmente para acompanhar o crescimento do seu negócio e tomar decisões baseadas em dados.
                </p>
              </div>
              
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-2">💡 Eficiência</h3>
                <p className="text-sm text-orange-800">
                  Mantenha sempre atualizado o cadastro de clientes e use a importação em massa para cadastrar muitos produtos de uma vez.
                </p>
              </div>

              <div className="p-4 bg-pink-50 rounded-lg border border-pink-100">
                <h3 className="font-semibold text-pink-900 mb-2">💡 Estoque</h3>
                <p className="text-sm text-pink-800">
                  Vincule produtos ao estoque para que o sistema subtraia automaticamente quando um produto for vendido. Configure alertas de estoque baixo!
                </p>
              </div>

              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <h3 className="font-semibold text-indigo-900 mb-2">💡 Backup</h3>
                <p className="text-sm text-indigo-800">
                  Exporte regularmente seu catálogo para CSV como backup. Use a exportação antes de fazer grandes alterações.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Atalhos de Teclado */}
        <Card>
          <CardHeader>
            <CardTitle>⌨️ Atalhos de Teclado Úteis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Ctrl + K</p>
                <p className="text-sm text-muted-foreground">Busca rápida global</p>
              </div>
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Ctrl + S</p>
                <p className="text-sm text-muted-foreground">Salvar alterações</p>
              </div>
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Ctrl + P</p>
                <p className="text-sm text-muted-foreground">Imprimir documento</p>
              </div>
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Ctrl + E</p>
                <p className="text-sm text-muted-foreground">Exportar para CSV</p>
              </div>
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Escape</p>
                <p className="text-sm text-muted-foreground">Fechar modal ou diálogo</p>
              </div>
              <div className="p-3 bg-muted rounded border">
                <p className="font-mono text-sm font-semibold mb-1">Tab</p>
                <p className="text-sm text-muted-foreground">Navegar entre campos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Glossário */}
        <Card>
          <CardHeader>
            <CardTitle>📚 Glossário de Termos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Pedido</h3>
                <p className="text-sm text-muted-foreground">Registro de uma venda com cliente, produtos/serviços, valores e datas de entrega. Pode ter múltiplos status durante o processo.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Orçamento</h3>
                <p className="text-sm text-muted-foreground">Proposta de preço que pode ser compartilhada com o cliente antes de virar um pedido. Pode ser convertido em pedido com um clique.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Catálogo</h3>
                <p className="text-sm text-muted-foreground">Lista de todos os seus produtos e serviços cadastrados que podem ser vendidos aos clientes.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Estoque</h3>
                <p className="text-sm text-muted-foreground">Inventário de materiais e produtos. Pode ser vinculado a produtos do catálogo para controle automático.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Vinculação</h3>
                <p className="text-sm text-muted-foreground">Associação entre um produto do catálogo e um item de estoque. Permite subtração automática ao vender.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Status do Pedido</h3>
                <p className="text-sm text-muted-foreground">Etapa atual do pedido: Aguardando (não iniciado), Em produção, Pronto, Entregue, ou Cancelado.</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Forma de Pagamento</h3>
                <p className="text-sm text-muted-foreground">Método usado para pagamento: Pendente (não pago), Parcial (parte paga), ou Pago (100% pago).</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Fluxo de Caixa</h3>
                <p className="text-sm text-muted-foreground">Movimento de dinheiro da sua empresa: entradas (vendas) e saídas (despesas e custos).</p>
              </div>
              <div className="border-b pb-3">
                <h3 className="font-semibold text-sm">Nota Fiscal (NF-e)</h3>
                <p className="text-sm text-muted-foreground">Documento fiscal obrigatório para vendas. Emitido digitalmente e integrado com o governo.</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Relatório</h3>
                <p className="text-sm text-muted-foreground">Análise de dados do seu negócio: vendas, financeiro, estoque, desempenho com gráficos e números.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recursos Educacionais */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              📖 Recursos Educacionais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">🎥 Canal YouTube</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Assista a vídeos tutoriais no nosso canal oficial com dicas e guias passo a passo.
                </p>
                <Button
                  size="sm"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => window.open('https://www.youtube.com/@AteliêPRO', '_blank')}
                >
                  <Video className="w-4 h-4 mr-2" />
                  Acessar YouTube
                </Button>
              </div>

              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">📖 Documentação Completa</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Guia detalhado com screenshots de como usar cada ferramenta do sistema.
                </p>
                <Button
                  size="sm"
                  className="w-full"
                  variant="outline"
                >Ler Documentação</Button>
              </div>

              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">💼 Casos de Uso</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Exemplos reais de como outros ateliers usam o Ateliê PRO para gerenciar seus negócios.
                </p>
                <Button
                  size="sm"
                  className="w-full"
                  variant="outline"
                >Ver Casos de Uso</Button>
              </div>

              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">🎓 Webinars</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Sessões ao vivo com especialistas para aprender dicas avançadas e melhorar sua produtividade.
                </p>
                <Button
                  size="sm"
                  className="w-full"
                  variant="outline"
                >Próximos Webinars</Button>
              </div>

              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">💡 Blog de Dicas</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Artigos sobre gestão de negócios, produtividade e melhores práticas para ateliers.
                </p>
                <Button
                  size="sm"
                  className="w-full"
                  variant="outline"
                >Ler Blog</Button>
              </div>

              <div className="p-4 border-2 border-purple-200 rounded-lg hover:shadow-lg hover:border-purple-400 transition-all bg-gradient-to-br from-purple-50 to-pink-50">
                <h3 className="font-semibold mb-2 text-purple-900">🤝 Comunidade</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Conecte-se com outros usuários no nosso grupo do WhatsApp.
                </p>
                <Button
                  size="sm"
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => window.open('https://chat.whatsapp.com/CyOtwlgXpwS581Em78kuc0', '_blank')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Entrar no Grupo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Suporte Premium */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              ⭐ Suporte Premium
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border border-purple-100">
                <h3 className="font-semibold text-purple-900 mb-2">Usuários Premium</h3>
                <ul className="text-sm text-purple-700 space-y-2">
                  <li>✓ Suporte prioritário via WhatsApp</li>
                  <li>✓ Atendimento em até 1 hora</li>
                  <li>✓ Sessão de onboarding pessoal</li>
                  <li>✓ Consultoria de negócios</li>
                  <li>✓ Grupo VIP no WhatsApp</li>
                </ul>
              </div>

              <div className="p-4 bg-white rounded-lg border border-purple-100">
                <h3 className="font-semibold text-purple-900 mb-2">Usuários Gratuitos</h3>
                <ul className="text-sm text-purple-700 space-y-2">
                  <li>✓ Suporte por email</li>
                  <li>✓ Atendimento em até 24 horas</li>
                  <li>✓ Central de Ajuda completa</li>
                  <li>✓ FAQ e Vídeos</li>
                  <li>✓ Comunidade de usuários</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-white rounded-lg border border-purple-100">
              <p className="text-sm text-purple-900 mb-3">
                <strong>Quer desbloquear suporte premium?</strong> Escolha um plano pago e ganhe acesso a atendimento exclusivo e recursos avançados.
              </p>
              <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => navigate('/assinatura')}>
                Ver Planos Premium
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Informações de Segurança */}
        <Card>
          <CardHeader>
            <CardTitle>🔒 Segurança e Privacidade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2">🔐 Seus dados estão seguros?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Sim! Usamos criptografia SSL/TLS, armazenamento em servidor seguro e backup automático diário.
                </p>
                <Button variant="outline" size="sm">Política de Privacidade</Button>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2">🛡️ Como você usa meus dados?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Usamos seus dados apenas para funcionar o sistema. Nunca vendemos ou compartilhamos seus dados com terceiros.
                </p>
                <Button variant="outline" size="sm">Termos de Serviço</Button>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2">📲 Posso deletar minha conta?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Sim! Você pode solicitar a exclusão de sua conta e todos os dados associados em "Minha Conta".
                </p>
                <Button variant="outline" size="sm">Como deletar minha conta</Button>
              </div>

              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2">🌐 Em qual país meu dados estão?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Seus dados estão armazenados em servidores seguros no Brasil com redundância e backup.
                </p>
                <Button variant="outline" size="sm">Infraestrutura</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer com CTA */}
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="pt-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-green-900 mb-2">
                Ainda tem dúvidas?
              </h2>
              <p className="text-green-800 mb-6">
                Nosso time está pronto para ajudar! Entre em contato conosco agora.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleWhatsAppContact}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Falar no WhatsApp
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleEmailContact}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Email
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/')}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Voltar ao Dashboard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
